import BaseService from "../../base/base_service";
import Event from '../../utils/event';
import { SYSTEM_EVENTS } from "../../events/events";
import { FP_VOUCHER_TYPES } from "../../../../common/const/free_play_consts";
import { FreePlayVoucherList } from "../free_play/free_play_voucher_list";
import IThirdPartyFreePlayService from "./third_party_free_play_service_interface";
import _ from '../../utils/underscore';
import { GAMES_INFO } from "../games_information/games_information_service_interface";
import { GNWP_SOURCES } from "../../components/external_links/external_links_consts";
import {IFreePlayVoucherData} from "../../../../common/interfaces/free-play/free-play-voucher-data.interface";

interface ThirdPartyFreePlayServiceParams {
    uri: string;
    enabled: boolean;
}

export interface FreePlayResponse {
    FreePlayBonusList: FreePlayDataInfo[];
}

interface IFreeSpinsInfo {
    BetPerSpin: number;

    NumberOfAwardedFreeSpins: number;

    NumberOfFreeSpinsLeft: number;
}

interface IVoucherInfo {
    RemainingFreePlayAmount: number;
}

enum FREE_PLAY_TYPE {FreePlay = 1, Voucher = 2}

interface FreePlayDataInfo {
    CompanyID: number;
    CurrencyCode: string;
    CurrentWinningAmount: number;
    ExpirationDate: Date;
    FreePlayID: number;
    FreePlayTypeID: FREE_PLAY_TYPE;
    GameTypesIDs: number[];
    BonusStatus: number;
    AwardedFreePlayAmount: number;
    FreeSpinsInfo?: IFreeSpinsInfo;
    VoucherInfo?: IVoucherInfo;
}


export default class ThirdPartyFreePlayService extends BaseService implements IThirdPartyFreePlayService {

    static CLASS = 'ThirdPartyFreePlayService';

    _serviceUri: string = '';

    _enabled = false;

    private _thirdPartyVouchers = new FreePlayVoucherList();

    protected init(params: ThirdPartyFreePlayServiceParams): void {
        super.init(params);
        this._serviceUri = params.uri || '';
        if (!this._serviceUri.endsWith('/')) {
            this._serviceUri += '/';
        }
        this._enabled = params.enabled ? params.enabled : false;
        this._subscribe();
    }

    private _subscribe() {
        if (this._enabled) {
            this.onSystemEvent([SYSTEM_EVENTS.OAUTH_STATE_CHANGE, SYSTEM_EVENTS.ON_INIT_COMPLETE], this.refreshVouchers);
            this.onSystemEvent([SYSTEM_EVENTS.CLOSE_GAME], this.refreshVouchers);
            this.onSystemEvent([SYSTEM_EVENTS.EXTERNAL_LINK_CLOSED], (linkSource) => {
                if (GNWP_SOURCES.MY_ACCOUNT === linkSource || GNWP_SOURCES.OMG === linkSource || GNWP_SOURCES.CASHIER === linkSource) {
                    this.refreshVouchers();
                }
            });

            this.onSystemEvent(SYSTEM_EVENTS.DEPOSIT_WIDGET_CLOSED, this.refreshVouchers)
        }
    }

    refreshVouchers(): Promise<IFreePlayVoucherData[]> {
        if (!this.serviceFactory.oAuth2Service().isAuthorized()) {
            // reset stored voucher info
            this._updateThirdPartyVouchers([]);
            return Promise.resolve([]);
        } else {
            return this._getThirdPartyVouchers();
        }
    }

    private _updateThirdPartyVouchers(vouchers: IFreePlayVoucherData[]) {
        this._thirdPartyVouchers.reset(vouchers);
        Event.fire(SYSTEM_EVENTS.SESSION_EVENT_THIRD_PARTY_FREE_PLAY_UPDATED, vouchers);
    }

    private _convertToVoucher(voucherData: FreePlayDataInfo, gamesInfo: GAMES_INFO): IFreePlayVoucherData {
        const activeCurrency = this.serviceFactory.currencyService().getCurrency();
        if (!voucherData.CurrencyCode || voucherData.CurrencyCode !== activeCurrency) {
            throw new Error(`Received third party voucher currency ${voucherData.CurrencyCode} is different than current currency ${activeCurrency}`);
        }

        const gameTypes: number[] = voucherData.GameTypesIDs && _.isArray(voucherData.GameTypesIDs) ? voucherData.GameTypesIDs : [];
        const availableGames: number[] = gameTypes.filter(gt => gamesInfo[gt] != null);
        const betAmounts: number[] = availableGames.map(gt => gamesInfo[gt].maxBet);

        if (voucherData.FreePlayTypeID === FREE_PLAY_TYPE.FreePlay) {
            if (!voucherData.FreeSpinsInfo) {
                throw new Error(`Received voucher ${voucherData.FreePlayID} has no FreeSpinsInfo.`);
            }

            // normally we should get bet per spin from backend
            let spinBetAmount = voucherData.FreeSpinsInfo.BetPerSpin;
            if (!spinBetAmount) {
                // maxBetAmount information is required since:
                // 1) in _formatVoucherUnit() for free play formula is value = value / maxBet;
                // 2) in _filterGamesByMinBet() it is required that CurrentBallance >= minBet
                const maxBetAmount = Math.max(...betAmounts);
                spinBetAmount = maxBetAmount;
            }

            const voucher: IFreePlayVoucherData = {
                ID: voucherData.FreePlayID.toString(),
                CurrentBalance: voucherData.FreeSpinsInfo.NumberOfFreeSpinsLeft * spinBetAmount,
                Winnings: Number(voucherData.CurrentWinningAmount),
                FreePlayTypeId: FP_VOUCHER_TYPES.FreeSpin,
                OriginAmount: voucherData.FreeSpinsInfo.NumberOfAwardedFreeSpins * spinBetAmount,
                ExpirationDate: new Date(voucherData.ExpirationDate),
                SpinBetAmount: spinBetAmount,
                GameTypeIDList: availableGames,
                CompanyID: voucherData.CompanyID
            };
            return voucher;
        }

        if (voucherData.FreePlayTypeID === FREE_PLAY_TYPE.Voucher) {
            if (!voucherData.VoucherInfo) {
                throw new Error(`Received voucher ${voucherData.FreePlayID} has no VoucherInfo.`);
            }

            const voucher: IFreePlayVoucherData = {
                ID: voucherData.FreePlayID.toString(),
                CurrentBalance: voucherData.VoucherInfo.RemainingFreePlayAmount,
                Winnings: Number(voucherData.CurrentWinningAmount),
                FreePlayTypeId: FP_VOUCHER_TYPES.FreePlay,
                OriginAmount: voucherData.AwardedFreePlayAmount,
                ExpirationDate: new Date(voucherData.ExpirationDate),
                SpinBetAmount: 0,
                GameTypeIDList: availableGames,
                CompanyID: voucherData.CompanyID
            };
            return voucher;
        }

        throw new Error(`Received third party voucher type ${voucherData.FreePlayTypeID} is not supported.`);
    }

    getStoredFreePlays(): IFreePlayVoucherData[] {
        return this._thirdPartyVouchers.values();
    }

    getFreePlays(): Promise<IFreePlayVoucherData[]> {
        return this._getThirdPartyVouchers();
    }

    getFreePlaysForGameType(gameType: number): Promise<IFreePlayVoucherData[]> {
        return this._getThirdPartyVouchers()
            .then(vouchers => {
                return vouchers.filter(voucher => voucher && voucher.GameTypeIDList.includes(gameType));
            });
    }

    private _httpGetThirdPartyVouchers(): Promise<IFreePlayVoucherData[]> {
        // make sure we don't call API in case of !enabled
        if (!this._enabled) {
            this.logger.info(`Prevented getting third party vouchers in the case service is not enabled`);
            return Promise.resolve([]);
        }

        return this.serviceFactory.http2Service().post<FreePlayResponse>(this._serviceUri + 'GetAllVouchers')
            .then((backendVouchers: FreePlayResponse) => {
                if (!backendVouchers.FreePlayBonusList
                    || !_.isArray(backendVouchers.FreePlayBonusList)
                    || backendVouchers.FreePlayBonusList.length === 0) {
                    return Promise.resolve([]);
                }

                // compose all game type list
                const allGameTypes: number[] = this._getAllGameTypes(backendVouchers);

                // filter available vouchers according to current platform information through gamesInformationService
                return this.serviceFactory.gamesInformationService().getGamesInfo(allGameTypes)
                .then((gamesInfo: GAMES_INFO) => {
                    const vouchers: IFreePlayVoucherData[] = [];
                    backendVouchers.FreePlayBonusList.forEach( backEndVoucher => {
                        let voucher = null;
                        try {
                            voucher = this._convertToVoucher(backEndVoucher, gamesInfo);
                        } catch (ex) {
                            this.logger.error('Cannot convert backend voucher ', backEndVoucher, ' exception is ', ex);
                        }

                        // filter only vouchers with specified game types
                        if (voucher && voucher.GameTypeIDList.length > 0) {
                            vouchers.push(voucher);
                        }
                    });
                    return vouchers;
                });
        });
    }

    private _getThirdPartyVouchers(): Promise<IFreePlayVoucherData[]> {
        return this._httpGetThirdPartyVouchers().then(vouchers => {
            this._updateThirdPartyVouchers(vouchers);
            return vouchers;
        });
    }

    private _getAllGameTypes(backendVouchers: FreePlayResponse) {
        const allGameTypes: number[] = [];
        if (backendVouchers && _.isArray(backendVouchers.FreePlayBonusList)) {
            backendVouchers.FreePlayBonusList.forEach(voucher => {
                if (_.isArray(voucher.GameTypesIDs)) {
                    voucher.GameTypesIDs.forEach(gt => {
                        const index = allGameTypes.findIndex(item => item === gt);
                        if (index < 0) {
                            allGameTypes.push(gt);
                        }
                    });
                }
            });
        }
        return allGameTypes;
    }
}
